import { Grid, FormLabel, RadioGroup, FormControlLabel, Radio, MenuItem, TextField, Tooltip, InputAdornment, Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { allBlankettOptions } from 'src/modules/sending/functions/blankettOptions';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useInvoiceTemplateType } from '../hooks/useInvoiceTemplateType';

type FormatSectionProps = {
  readOnly?: boolean;
};

export const FormatSection = ({ readOnly = false }: FormatSectionProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const { isSwish } = useInvoiceTemplateType();
  // Behåll mallens sparade blankett som val även om den inte längre ingår i kontotypens val (t.ex. card eller agavi),
  // så att den inte byts ut i tysthet när mallen sparas.
  // const savedFormTemplate = getValues('formTemplate') as string | undefined;
  const blankettOptions = [...allBlankettOptions];
  /*
  if (savedFormTemplate && !blankettOptions.some((o) => o.code === savedFormTemplate)) {
    blankettOptions.unshift({ code: savedFormTemplate, label: getValues('formTemplateText') || savedFormTemplate, description: '' });
  }
*/

  return (
    <Grid container>
      <Box sx={{ width: '100%' }}>
        <Grid container mt={1} spacing={2} justifyContent='space-between'>
          {/* Swishmallar skriver ingen avi, så blankettvalet gör ingenting där. */}
          {!isSwish && (
            <>
              <Grid item xs={12} sm={3} mb={1}>
                <Controller
                  name='formTemplate'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        field.onChange(e);
                        // Visa vald blanketts namn i den låsta Blankettmall-rutan.
                        const selected = blankettOptions.find((o) => o.code === e.target.value);
                        let text = selected?.label ?? e.target.value;
                        // Flexibelt belopp: visa mallens beloppsintervall.
                        if (e.target.value === 'bg42') {
                          text += ` (mellan ${getValues('payIntervalFrom')} och ${getValues('payIntervalTom')} kronor)`;
                        }
                        setValue('formTemplateText', text);
                      }}
                      select
                      disabled={readOnly}
                      fullWidth
                      size='small'
                      label={t('invoice:adminInvoiceTemplatePage.templateInformation.changeFormTemplate')}
                    >
                      {blankettOptions.map((o) => (
                        <MenuItem key={o.code} value={o.code}>
                          {o.code}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={9} mb={1}>
                <ControlledTextField
                  disabled
                  name='formTemplateText'
                  label={t('invoice:adminInvoiceTemplatePage.templateInformation.formTemplate')}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Grid item xs={12}>
              <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormLabel id='label-printZeroInvoice' sx={{ minWidth: '50%' }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.printInvoicesWithZeroAmount')}
                </FormLabel>
                <Controller
                  name='printZeroInvoice'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      aria-labelledby='label-printZeroInvoice'
                      name={field.name}
                      value={!!field.value}
                      // RadioGroup ger värdet som sträng – formuläret ska ha boolean.
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                    >
                      <FormControlLabel value disabled={readOnly} control={<Radio />} label={t('common:button.yes')} />
                      <FormControlLabel value={false} disabled={readOnly} control={<Radio />} label={t('common:button.no')} />
                    </RadioGroup>
                  )}
                />
              </Grid>
              <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormLabel id='label-householdInvoice' sx={{ minWidth: '50%' }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.oneCollectiveInvoicePerHousehold')}
                </FormLabel>
                <Controller
                  name='householdInvoice'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      aria-labelledby='label-householdInvoice'
                      name={field.name}
                      value={!!field.value}
                      // RadioGroup ger värdet som sträng – formuläret ska ha boolean.
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                    >
                      <FormControlLabel value disabled={readOnly} control={<Radio />} label={t('common:button.yes')} />
                      <FormControlLabel value={false} disabled={readOnly} control={<Radio />} label={t('common:button.no')} />
                    </RadioGroup>
                  )}
                />
              </Grid>
              <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormLabel id='label-sendAcknowledgement' sx={{ minWidth: '50%' }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.sendAcknowledgement')}
                </FormLabel>
                <Controller
                  name='suppressAcknowledgement'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      aria-labelledby='label-sendAcknowledgement'
                      name={field.name}
                      // Omvänt: "Skicka godkännandemail" Ja = suppressAcknowledgement false.
                      value={!field.value}
                      onChange={(e) => field.onChange(e.target.value !== 'true')}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                    >
                      <FormControlLabel value disabled={readOnly} control={<Radio />} label={t('common:button.yes')} />
                      <FormControlLabel value={false} disabled={readOnly} control={<Radio />} label={t('common:button.no')} />
                    </RadioGroup>
                  )}
                />
              </Grid>
              <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormLabel id='label-approvePaymentsGreaterThanInvoiced' sx={{ minWidth: '50%' }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.approvePaymentsGreaterThanInvoiced')}
                </FormLabel>
                <Controller
                  name='approvePaymentsGreaterThanInvoiced'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      aria-labelledby='label-approvePaymentsGreaterThanInvoiced'
                      name={field.name}
                      value={!!field.value}
                      // RadioGroup ger värdet som sträng – formuläret ska ha boolean.
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                    >
                      <FormControlLabel value disabled={readOnly} control={<Radio />} label={t('common:button.yes')} />
                      <FormControlLabel value={false} disabled={readOnly} control={<Radio />} label={t('common:button.no')} />
                    </RadioGroup>
                  )}
                />
              </Grid>
              {/* Beloppsintervall (kronor) för flexibelt belopp. 0 eller -1 = ingen gräns. */}
              <Grid container spacing={2} mt={1}>
                {(['payIntervalFrom', 'payIntervalTom'] as const).map((name) => (
                  <Grid item xs={12} sm={3} key={name}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          // Tomt fält sparas som 0 (ingen gräns).
                          onChange={(e) => field.onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                          type='number'
                          disabled={readOnly}
                          fullWidth
                          size='small'
                          InputProps={{
                            inputProps: { step: 1 },
                            endAdornment: (
                              <InputAdornment position='end'>
                                <Tooltip
                                  arrow
                                  title={
                                    <Box sx={{ whiteSpace: 'pre-line' }}>
                                      {t('invoice:adminInvoiceTemplatePage.templateInformation.payIntervalDescription')}
                                    </Box>
                                  }
                                >
                                  <InfoOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          label={t(`invoice:adminInvoiceTemplatePage.templateInformation.${name}`)}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};
